import React from 'react'
import { isEqual } from 'lodash'
import 'rc-steps/assets/index.css'

import AddCompetitorComponent from '../../../components/competitorTracking/AddCompetitorComponent'
import {
  kissmetricsRecordingEvent,
  KISSMETRICS_TRACK_EVENTS,
} from '../../../utils/kissmetrics'
import { translate } from '../../../utils/translation'
import { VALID_STATUS } from '../../../components/input/TagInput/CompetitorTagInput'
import ModalDialog from '../../../components/ui/modal-dialog'
import { LeadCaptureContainer } from '../../../features/lead-capture/components/lead-capture-container'
import { useLeadCaptureStore } from '../../../features/lead-capture/store/lead-capture-store'

import { StepWrapper, NavButtons, NavButton, StepContent } from './styled'

export default class Step4 extends React.Component {
  constructor(props) {
    super(props)

    const { locations, competitors, MAX_COMPETITORS_PER_PROJECT } = this.props

    this.state = {
      locations,
      selectedLocations: locations,
      invalidCompetitors:
        competitors.length * locations.length > MAX_COMPETITORS_PER_PROJECT,
      invalidLocations: false,
      isLeadCaptureOpen: false,
    }

    this.unsubscribe = null
  }

  componentDidMount() {
    this.unsubscribe = useLeadCaptureStore.subscribe(
      (state) => state.isModalOpen,
      (isModalOpen) => {
        this.setState({ isLeadCaptureOpen: isModalOpen })
      },
    )
  }

  componentWillUnmount() {
    this.unsubscribe?.()
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!isEqual(prevState.locations, nextProps.locations)) {
      return {
        locations: nextProps.locations,
        selectedLocations: nextProps.locations,
        invalidCompetitors:
          nextProps.competitors.length * nextProps.locations.length >
          nextProps.MAX_COMPETITORS_PER_PROJECT,
        invalidLocations:
          nextProps.locations.length === 0 ||
          nextProps.locations.length >
            nextProps.MAX_COMPETITORS_LOCATIONS_PER_PROJECT,
      }
    }
    return null
  }

  setNewCompetitors = (newCompetitors) => {
    const { selectedLocations } = this.state
    const {
      competitors,
      showUpgradeModal,
      setCompetitors,
      MAX_COMPETITORS_PER_PROJECT,
    } = this.props
    const totalCount = newCompetitors.length * selectedLocations.length

    if (totalCount > MAX_COMPETITORS_PER_PROJECT) {
      if (newCompetitors.length > competitors.length)
        showUpgradeModal('step4_reached_maximum_competitors')
    }
    setCompetitors(newCompetitors)
    this.setState({
      invalidCompetitors: totalCount > MAX_COMPETITORS_PER_PROJECT,
    })
  }

  setLocations = (locations) => {
    const { competitors, showUpgradeModal } = this.props

    const totalCount = competitors.length * locations.length
    const {
      MAX_COMPETITORS_PER_PROJECT,
      MAX_COMPETITORS_LOCATIONS_PER_PROJECT,
    } = this.props
    this.setState({
      invalidCompetitors: totalCount > MAX_COMPETITORS_PER_PROJECT,
      invalidLocations:
        locations.length === 0 ||
        locations.length > MAX_COMPETITORS_LOCATIONS_PER_PROJECT,
      selectedLocations: locations,
    })
    if (
      totalCount > MAX_COMPETITORS_PER_PROJECT ||
      locations.length > MAX_COMPETITORS_LOCATIONS_PER_PROJECT
    ) {
      showUpgradeModal('step4_reached_maximum_competitors')
    }
  }

  handleCloseModal = () => {
    useLeadCaptureStore.setState({ isModalOpen: false, currentView: 'initial' })
  }

  handleNextClick = () => {
    const { competitors, showUpgradeModal } = this.props
    const { invalidCompetitors, invalidLocations } = this.state

    if (competitors.length === 0) {
      return
    }

    if (!invalidCompetitors && !invalidLocations) {
      kissmetricsRecordingEvent(
        KISSMETRICS_TRACK_EVENTS.project_creation_actions,
        {
          'project flow action': 'competitor add next',
        },
      )

      // Show lead capture modal before proceeding to next step
      useLeadCaptureStore.setState({ isModalOpen: true })
      return
    }

    if (invalidCompetitors) {
      showUpgradeModal()
    }
  }

  handleSkipClick = () => {
    kissmetricsRecordingEvent(
      KISSMETRICS_TRACK_EVENTS.project_creation_actions,
      {
        'project flow action': 'competitor add skip',
      },
    )

    useLeadCaptureStore.setState({ isModalOpen: true })
  }

  handleLeadCaptureDiyPath = () => {
    const { submitStep6 } = this.props
    submitStep6(null)
  }

  render() {
    const {
      pendingData,
      suggestedCompetitors,
      previousStep,
      competitors,
      newAddonProject,
      domain,
      locations,
      openEditLocationsModal,
      submitStep6,
      trackChooseYourSuccessPathView,
      trackChooseYourSuccessPathCTAClicked,
    } = this.props
    const {
      invalidCompetitors,
      selectedLocations,
      invalidLocations,
      isLeadCaptureOpen,
    } = this.state

    const hasInvalidOrValidating = competitors.some(
      (c) =>
        c.status === VALID_STATUS.invalid ||
        c.status === VALID_STATUS.validating,
    )

    return (
      <StepWrapper width="100%">
        <StepContent>
          <AddCompetitorComponent
            domain={domain}
            competitorsInProject={[]}
            locations={locations}
            pendingData={pendingData}
            suggestedCompetitors={suggestedCompetitors}
            competitorsToAdd={competitors}
            setCompetitorsToAdd={this.setNewCompetitors}
            selectedLocations={selectedLocations}
            setSelectedLocations={this.setLocations}
            areLocationsInvalid={invalidLocations}
            areCompetitorsInvalid={invalidCompetitors}
            newAddonProject={newAddonProject}
            openEditLocationsModal={openEditLocationsModal}
          />
        </StepContent>
        <NavButtons>
          <NavButton
            testId="add-project-step4-back"
            color="grey"
            onClick={previousStep}
            tag={translate('back')}
          />
          <NavButton
            testId="add-project-step4-skip"
            color="grey"
            onClick={this.handleSkipClick}
            tag={translate('skip')}
          />
          <NavButton
            testId="add-project-step4-next"
            color="orange"
            onClick={this.handleNextClick}
            tag={translate('next')}
            disabled={
              invalidCompetitors ||
              invalidLocations ||
              competitors.length === 0 ||
              hasInvalidOrValidating
            }
          />
        </NavButtons>

        <ModalDialog
          isOpen={isLeadCaptureOpen}
          onClose={this.handleCloseModal}
          variant="center"
          content={
            <LeadCaptureContainer
              diyPathOnClick={this.handleLeadCaptureDiyPath}
              onFormSubmit={(values) => {
                submitStep6(values)
              }}
              trackChooseYourSuccessPathView={trackChooseYourSuccessPathView}
              trackChooseYourSuccessPathCTAClicked={
                trackChooseYourSuccessPathCTAClicked
              }
            />
          }
          closeButton={false}
          maxSize="tw-max-w-[1180px]"
          zIndex="tw-z-[100001]"
          containerClassName="tw-bg-white"
          closeOnBackdropClick={false}
        />
      </StepWrapper>
    )
  }
}
